/**
 * Jobs OpenAPI Specification
 *
 * Generates OpenAPI 3.0 spec for all jobs sub-domain endpoints.
 * Each sub-contract is generated individually so that path keys match
 * the actual Express mount points (/job-categories, /job-list, /job-priorities).
 * The /jobs prefix is added by the combined openapi.ts via prefixPaths().
 */

import type { AppRoute } from '@ts-rest/core';
import { generateOpenApi } from '@ts-rest/open-api';
import type { OperationObject } from 'openapi3-ts';
import { activityLogContract } from './activity-log/index.js';
import { dataCaptureContract } from './data-capture/index.js';
import { dfeMaterialsContract } from './dfe-materials/index.js';
import { dfeContract } from './dfe/index.js';
import { jobDocumentsContract } from './documents/index.js';
import { jobCategoriesContract } from './job-categories/index.js';
import { jobCategoryPrefillTextContract } from './job-category-prefill-text/index.js';
import { jobDetailContract } from './job-detail/index.js';
import { jobListContract, jobListExtendedContract } from './job-list/index.js';
import { jobPrioritiesContract } from './job-priorities/index.js';
import { permitStatusContract } from './permit-status/index.js';
import { workOrderStatusListContract } from './work-order-status-list/index.js';
import { updateWorkOrderStatusContract } from './work-orders/updateWorkOrderStatus.contract.js';
import { workOrderDirectionalContract } from './work-orders/workOrderDirectional.contract.js';

const baseInfo = {
  info: {
    title: 'Skewb Works Jobs API',
    version: '1.0.0',
    description:
      'Type-safe jobs API for Skewb Works application. All endpoints use Zod validation and return typed responses.',
    contact: {
      name: 'Skewb Works Development Team',
    },
  },
  servers: [
    { url: 'https://localhost:8080', description: 'Local development server' },
    { url: 'https://api.works-works-lite.dev.skewbdigital.co.uk', description: 'Dev API server' },
    {
      url: 'https://api.works-works-lite.dev.skewbdigital.co.uk',
      description: 'Production API server',
    },
  ],
  tags: [{ name: 'Jobs', description: 'Job list, categories, and priorities endpoints' }],
};

const operationMapper = {
  setOperationId: true,
  operationMapper: (operation: OperationObject, _appRoute: AppRoute): OperationObject => ({
    ...operation,
    tags: ['Jobs'],
  }),
} as const;

const workOrdersOperationMapper = {
  setOperationId: true,
  operationMapper: (operation: OperationObject, _appRoute: AppRoute): OperationObject => ({
    ...operation,
    tags: ['Work Orders'],
  }),
} as const;

const jobCategoriesSpec = generateOpenApi(jobCategoriesContract, baseInfo, operationMapper);
const jobListSpec = generateOpenApi(jobListContract, baseInfo, operationMapper);
const jobListExtendedSpec = generateOpenApi(jobListExtendedContract, baseInfo, operationMapper);
const jobPrioritiesSpec = generateOpenApi(jobPrioritiesContract, baseInfo, operationMapper);
const workOrderStatusListSpec = generateOpenApi(
  workOrderStatusListContract,
  baseInfo,
  operationMapper,
);
const permitStatusSpec = generateOpenApi(permitStatusContract, baseInfo, operationMapper);
const workOrderDirectionalSpec = generateOpenApi(
  workOrderDirectionalContract,
  baseInfo,
  workOrdersOperationMapper,
);
const updateWorkOrderStatusSpec = generateOpenApi(
  updateWorkOrderStatusContract,
  baseInfo,
  operationMapper,
);
const jobDetailSpec = generateOpenApi(jobDetailContract, baseInfo, operationMapper);
const activityLogSpec = generateOpenApi(activityLogContract, baseInfo, operationMapper);
const jobDocumentsSpec = generateOpenApi(jobDocumentsContract, baseInfo, operationMapper);
const dataCaptureSpec = generateOpenApi(dataCaptureContract, baseInfo, operationMapper);
const dfeSpec = generateOpenApi(dfeContract, baseInfo, operationMapper);
const jobCategoryPrefillTextSpec = generateOpenApi(
  jobCategoryPrefillTextContract,
  baseInfo,
  operationMapper,
);
const dfeMaterialsSpec = generateOpenApi(dfeMaterialsContract, baseInfo, operationMapper);

/**
 * OpenAPI 3.0 specification for the Jobs API.
 *
 * Paths use kebab-case keys matching the Express mount points so that
 * prefixPaths(jobsOpenApiSpec.paths, '/jobs') in openapi.ts produces:
 *   GET /jobs/job-categories
 *   GET /jobs/job-list
 *   GET /jobs/job-priorities
 *   GET /jobs/job-detail/:jobId
 *   GET /jobs/activity-log/:jobId
 *   GET /jobs/documents/:jobId
 *   GET /jobs/data-capture/:jobId
 *   GET /jobs/dfe/:jobId
 */
export const jobsOpenApiSpec = {
  openapi: jobCategoriesSpec.openapi,
  paths: {
    '/job-categories': jobCategoriesSpec.paths?.['/'] ?? {},
    '/job-list': jobListSpec.paths?.['/'] ?? {},
    '/job-list-extended': jobListExtendedSpec.paths?.['/'] ?? {},
    '/job-priorities': jobPrioritiesSpec.paths?.['/'] ?? {},
    '/work-order-status-list': workOrderStatusListSpec.paths?.['/'] ?? {},
    '/permit-status': permitStatusSpec.paths?.['/'] ?? {},
    ...updateWorkOrderStatusSpec.paths,
    '/job-detail/{jobId}': jobDetailSpec.paths?.['/{jobId}'] ?? {},
    '/activity-log/{jobId}': activityLogSpec.paths?.['/{jobId}'] ?? {},
    '/documents/{jobId}': jobDocumentsSpec.paths?.['/{jobId}'] ?? {},
    '/data-capture/{jobId}': dataCaptureSpec.paths?.['/{jobId}'] ?? {},
    '/dfe/{jobId}': dfeSpec.paths?.['/{jobId}'] ?? {},
    '/dfe-materials': dfeMaterialsSpec.paths?.['/'] ?? {},
    '/job-category-prefill-text': jobCategoryPrefillTextSpec.paths?.['/'] ?? {},
  },
  components: {
    schemas: {
      ...jobCategoriesSpec.components?.schemas,
      ...jobListSpec.components?.schemas,
      ...jobListExtendedSpec.components?.schemas,
      ...jobPrioritiesSpec.components?.schemas,
      ...workOrderStatusListSpec.components?.schemas,
      ...permitStatusSpec.components?.schemas,
      ...updateWorkOrderStatusSpec.components?.schemas,
      ...jobDetailSpec.components?.schemas,
      ...activityLogSpec.components?.schemas,
      ...jobDocumentsSpec.components?.schemas,
      ...dataCaptureSpec.components?.schemas,
      ...dfeSpec.components?.schemas,
      ...dfeMaterialsSpec.components?.schemas,
      ...jobCategoryPrefillTextSpec.components?.schemas,
    },
  },
  tags: baseInfo.tags,
};

/**
 * OpenAPI paths for directional work-order template endpoints.
 * Mounted at /works-lite/work-orders in apps/backend/src/routes/index.ts.
 */
export const workOrderDirectionalOpenApiSpec = {
  openapi: workOrderDirectionalSpec.openapi,
  paths: workOrderDirectionalSpec.paths ?? {},
  components: { schemas: workOrderDirectionalSpec.components?.schemas ?? {} },
};
